/**
 * A board that opens with no size chosen (blocks/btf-product-picker.liquid, "size open").
 *
 * The page is drawn that way by the server: the large board's photograph, "From £75", every size
 * cell unticked. Two things are left for the browser while it stays that way:
 *
 * 1. Add to basket asks for a size instead of adding one nobody picked.
 * 2. Another colour shows that colour's photographs with the size still open. Horizon's own
 *    picker would ask Shopify for the colour alone, and Shopify answers with that colour's first
 *    size as if it had been chosen — so the change is kept from Horizon and only the gallery is
 *    fetched, for the large board in that colour.
 *
 * The held-back change is announced as a bubbling "btf:open-colour" event on the colour input, so
 * a live preview (assets/btf-planner.js) can still redraw in the new colour.
 *
 * Ticking a size is Horizon's as usual, and from then on this file does nothing: the state is
 * simply "the prompt is on the page and no size is ticked".
 */

const PROMPT = '[data-btf-size-prompt]';
const SIZE_INPUTS = '.btf-picker__group--size input';
const ADD_BUTTONS = '[ref="addToCartButton"], button[name="add"], .sticky-add-to-cart__button';

/**
 * The open picker in the same section as an element, or null once a size is ticked.
 * @param {Element} from
 */
function openPicker(from) {
  const section = from.closest('.shopify-section');
  const prompt = section?.querySelector(PROMPT);
  const group = prompt?.closest('fieldset');
  if (!section || !(prompt instanceof HTMLElement) || !group) return null;
  if (group.querySelector('input:checked')) return null;
  return { section, prompt, group, picker: group.closest('variant-picker') };
}

/** @param {NonNullable<ReturnType<typeof openPicker>>} open */
function ask({ prompt, group }) {
  prompt.hidden = false;
  group.classList.add('is-asked');
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  group.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
  group.querySelector('input')?.focus({ preventScroll: true });
}

/**
 * The variant the buy form would add, which the share strip also reads for its photograph.
 * @param {Element} section
 * @param {string | undefined} variantId
 */
function setFormVariant(section, variantId) {
  const field = section.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  if (field instanceof HTMLInputElement && variantId) field.value = variantId;
}

/** @type {AbortController | undefined} */
let request;

/**
 * @param {HTMLInputElement} input - the colour just ticked
 * @param {NonNullable<ReturnType<typeof openPicker>>} open
 */
async function showColour(input, { section, picker }) {
  const variantId = input.dataset.btfOpenVariant;
  if (!(picker instanceof HTMLElement) || !picker.dataset.productUrl || !variantId) return;

  request?.abort();
  request = new AbortController();

  const url = new URL(picker.dataset.productUrl, window.location.origin);
  url.searchParams.set('variant', variantId);
  if (picker.dataset.sectionId) url.searchParams.set('section_id', picker.dataset.sectionId);

  try {
    const response = await fetch(url, { signal: request.signal });
    if (!response.ok) return;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    // A size ticked, or another colour, while this was on its way: that one wins
    if (!input.checked || !openPicker(input)) return;

    const gallery = section.querySelector('media-gallery');
    const newGallery = html.querySelector('media-gallery');
    if (gallery && newGallery) gallery.replaceWith(newGallery);

    // Each size cell now stands for that size in this colour
    for (const cell of section.querySelectorAll(SIZE_INPUTS)) {
      if (!(cell instanceof HTMLInputElement)) continue;
      const newCell = html.querySelector(`${SIZE_INPUTS}[data-option-value-id="${cell.dataset.optionValueId}"]`);
      if (!(newCell instanceof HTMLInputElement)) continue;
      if (newCell.dataset.variantId) cell.dataset.variantId = newCell.dataset.variantId;
      const price = cell.parentElement?.querySelector('.btf-picker__price');
      const newPrice = newCell.parentElement?.querySelector('.btf-picker__price');
      if (price && newPrice) price.textContent = newPrice.textContent;
    }

    setFormVariant(section, variantId);
  } catch (error) {
    if (!(error instanceof Error) || error.name !== 'AbortError') console.warn('[btf-size-first]', error);
  }
}

// On the window and in the capture phase, so these run before Horizon's own listeners on the
// document and on the picker.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(ADD_BUTTONS) : null;
    const open = button && openPicker(button);
    if (!open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    ask(open);
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const open = openPicker(form);
    if (!open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    ask(open);
  },
  true
);

window.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.btfOpenVariant) return;
    const open = openPicker(input);
    if (!open) return;
    event.stopImmediatePropagation();
    // Held back from Horizon, but a live preview on the page (the planner) still needs to know
    input.dispatchEvent(new CustomEvent('btf:open-colour', { bubbles: true }));
    showColour(input, open);
  },
  true
);

// The server drew the page with no size ticked. A browser restoring the form after a reload can
// tick one on its own, which would look chosen without being so.
for (const prompt of document.querySelectorAll(PROMPT)) {
  if (!(prompt instanceof HTMLElement)) continue;
  for (const input of prompt.closest('fieldset')?.querySelectorAll('input:checked') ?? []) {
    if (input instanceof HTMLInputElement) input.checked = false;
  }
  const section = prompt.closest('.shopify-section');
  if (section) setFormVariant(section, prompt.dataset.variantId);
}
